import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiArrowRight,
  FiPhoneCall,
  FiCheckCircle,
  FiPackage,
  FiTruck,
  FiAward,
  FiUsers,
  FiDollarSign,
  FiHeadphones,
  FiShield,
  FiToggleLeft,
  FiZap,
  FiLink2,
  FiGrid,
  FiTool,
  FiLayers,
  FiSettings,
  FiFlag,
  FiBriefcase,
  FiMessageSquare,
  FiList,
  FiFileText,
  FiShoppingCart,
  FiCheckSquare,
  FiPlus,
  FiMinus,
  FiAnchor,
} from 'react-icons/fi';

import Reveal from '../common/Reveal';
import Counter from '../common/Counter';
import { tradingCategories } from '../data/categories';

// ---------------------------------------------------------------------------
// Hero / About / CTA imagery
// ---------------------------------------------------------------------------
import tradingHeroImg from '../assest/trading hero.jpg.png';
import overviewImg from '../assest/ovreview.jpg.jpg';


// ---------------------------------------------------------------------------
// Content data
// ---------------------------------------------------------------------------
const aboutFeatures = [
  { icon: FiCheckCircle, title: 'Certified Products' },
  { icon: FiAward, title: 'Trusted Manufacturers' },
  { icon: FiPackage, title: 'Bulk Supply' },
  { icon: FiTruck, title: 'Nationwide Delivery' },
];

const productCategories = [
  {
    icon: FiAnchor,
    title: 'Pole Solutions',
    desc: 'Durable pole solutions designed for overhead transmission and distribution networks.',
    items: ['Steel Tubular Poles', 'PCC Poles'],
  },
  {
    icon: FiShield,
    title: 'Insulators',
    desc: 'Reliable insulation solutions for high-voltage and medium-voltage applications.',
    items: ['Polymer Insulators', 'Porcelain Insulators'],
  },
  {
    icon: FiToggleLeft,
    title: 'Switching Equipment',
    desc: 'High-performance switching equipment ensuring safe and reliable power distribution.',
    items: ['Air Break Switches', 'Isolators', 'Drop-Out Fuses'],
  },
  {
    icon: FiZap,
    title: 'Protection Equipment',
    desc: 'Advanced protection products safeguarding electrical systems against faults and surges.',
    items: ['Lightning Arresters', 'Earthing Materials'],
  },
  {
    icon: FiLink2,
    title: 'Cable Accessories',
    desc: 'Complete cable jointing and insulation solutions for dependable electrical connectivity.',
    items: ['Cable Jointing Kits', 'Heat Shrink Products'],
  },
  {
    icon: FiGrid,
    title: 'Transmission Hardware',
    desc: 'Comprehensive hardware solutions supporting transmission and distribution infrastructure.',
    items: ['Tower Hardware', 'Conductors', 'Transmission Line Accessories'],
  },
];

const whyChoose = [
  { icon: FiAward, title: 'High Quality Products', desc: 'Every product sourced meets strict quality benchmarks before it reaches your site.' },
  { icon: FiUsers, title: 'Trusted Supply Chain', desc: 'Long-standing relationships with certified manufacturers across the country.' },
  { icon: FiDollarSign, title: 'Competitive Pricing', desc: 'Direct sourcing keeps our pricing sharp on bulk and project-scale orders.' },
  { icon: FiTruck, title: 'Fast Delivery', desc: 'A dependable logistics network for on-time dispatch, anywhere in India.' },
  { icon: FiUsers, title: 'Experienced Team', desc: 'A skilled team with deep expertise across the EPC and power distribution sector.' },
  { icon: FiHeadphones, title: 'Technical Assistance', desc: 'Guidance on product selection, specifications and compliance requirements.' },
];

const industries = [
  { icon: FiZap, title: 'Power Utilities', desc: 'Supplying state and private power distribution utilities.' },
  { icon: FiTool, title: 'Electrical Contractors', desc: 'Reliable materials for contractors executing EPC projects.' },
  { icon: FiLayers, title: 'Infrastructure Projects', desc: 'Products for large-scale transmission and distribution infrastructure.' },
  { icon: FiSettings, title: 'Industrial Plants', desc: 'Dependable electrical components for industrial facilities.' },
  { icon: FiFlag, title: 'Government Organizations', desc: 'Certified products meeting government project specifications.' },
  { icon: FiBriefcase, title: 'Commercial Projects', desc: 'Quality electrical products for commercial developments.' },
];

const processSteps = [
  { icon: FiMessageSquare, title: 'Requirement Discussion' },
  { icon: FiList, title: 'Product Selection' },
  { icon: FiFileText, title: 'Quotation' },
  { icon: FiShoppingCart, title: 'Procurement' },
  { icon: FiCheckSquare, title: 'Quality Inspection' },
  { icon: FiTruck, title: 'Dispatch & Delivery' },
  { icon: FiHeadphones, title: 'After-Sales Support' },
];

const stats = [
  { value: 500, suffix: '+', label: 'Products Supplied' },
  { value: 100, suffix: '+', label: 'Trusted Clients' },
  { value: 17, suffix: '+', label: 'Years of Experience' },
  { value: 100, suffix: '%', label: 'Quality Commitment' },
];

const faqs = [
  {
    q: 'What products do you supply?',
    a: 'We supply a comprehensive range of electrical transmission and distribution products — poles, insulators, switching equipment, protection equipment, cable accessories and transmission hardware.',
  },
  {
    q: 'Do you deliver across India?',
    a: 'Yes. We offer nationwide delivery and dispatch to utilities, contractors and project sites across the country.',
  },
  {
    q: 'Can you fulfill bulk orders?',
    a: 'Yes. Our supply network is built to handle bulk and project-scale orders with dependable, confirmed timelines.',
  },
  {
    q: 'Do you provide technical consultation?',
    a: 'Yes. Our team offers guidance on product selection, specifications and compliance for your specific project.',
  },
  {
    q: 'How can I request a quotation?',
    a: 'Reach out through our contact page or call us directly with your requirement list, and we\u2019ll respond with a detailed quotation.',
  },
];

// Dot-grid background used behind a couple of sections for subtle texture.
const dotGridStyle = {
  backgroundImage: 'radial-gradient(rgba(18,10,59,0.08) 1px, transparent 1px)',
  backgroundSize: '22px 22px',
};

// Reusable big shimmer section heading — same pattern as home.js
function SectionHeading({ eyebrow, title, description, center = true, dark = false }) {
  return (
    <div className={center ? 'mx-auto max-w-3xl text-center' : ''}>
      <h2 className="break-words text-4xl font-extrabold uppercase leading-none sm:text-5xl lg:text-6xl">
        <span className="hero-shimmer block text-brand-orange">{eyebrow}</span>
      </h2>
      <h3 className={`mt-4 text-2xl font-bold sm:text-3xl lg:text-4xl ${dark ? 'text-white' : 'text-navy'}`}>
        {title}
      </h3>
      {description && (
        <p className={`mx-auto mt-5 max-w-2xl text-base leading-7 sm:text-lg ${dark ? 'text-white/80' : 'text-slate-600'}`}>
          {description}
        </p>
      )}
    </div>
  );
}

function FaqItem({ item, isOpen, onToggle }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left sm:px-6 sm:py-5"
      >
        <span className="text-sm font-bold text-navy sm:text-base">{item.q}</span>
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors duration-300 ${
            isOpen ? 'bg-brand-orange text-white' : 'bg-navy/5 text-navy'
          }`}
        >
          {isOpen ? <FiMinus size={16} /> : <FiPlus size={16} />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="px-5 pb-5 text-sm leading-relaxed text-slate-600 sm:px-6">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Trading() {
  const totalTradingProducts = tradingCategories.reduce((sum, c) => sum + c.items.length, 0);
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="bg-white">
      {/* ============================================================= */}
      {/* HERO                                                          */}
      {/* ============================================================= */}
      <section className="relative overflow-hidden text-white">
        <div className="absolute inset-0">
          <img
            src={tradingHeroImg}
            alt="Electrical transmission and distribution products"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#08162D]/75 via-[#08162D]/45 to-[#08162D]/15" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08162D]/20 via-transparent to-transparent" />
        </div>

        <div className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 animate-float rounded-full bg-brand-orange/10 blur-3xl" />

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28 lg:px-8 lg:py-36">
          <span className="inline-block animate-fadeInUp rounded-full bg-brand-orange/20 px-4 py-1 text-xs font-bold uppercase tracking-wide text-brand-orange ring-1 ring-brand-orange/30">
            Our Services — Trading
          </span>
          <h1
            className="mt-5 max-w-3xl animate-fadeInUp break-words text-4xl font-extrabold uppercase leading-none sm:text-5xl lg:text-6xl"
            style={{ animationDelay: '80ms' }}
          >
            <span className="hero-shimmer block text-white">Electrical Product</span>
            <span className="hero-shimmer block text-brand-orange">Trading Solutions</span>
          </h1>
          <p
            className="mt-6 max-w-2xl animate-fadeInUp text-base text-white/80 sm:text-lg"
            style={{ animationDelay: '160ms' }}
          >
            We supply a comprehensive range of high-quality electrical transmission and distribution
            products sourced from trusted manufacturers to support utilities, industries, infrastructure
            projects, and power distribution networks across India.
          </p>

          <div className="mt-8 flex flex-wrap gap-4 animate-fadeInUp" style={{ animationDelay: '240ms' }}>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition hover:-translate-y-0.5 hover:bg-brand-orangedark"
            >
              Request a Quote <FiArrowRight />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition hover:-translate-y-0.5 hover:border-white"
            >
              <FiPhoneCall /> Contact Us
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap gap-8 animate-fadeInUp" style={{ animationDelay: '320ms' }}>
            <div>
              <p className="text-2xl font-extrabold sm:text-3xl">
                <Counter value={totalTradingProducts} suffix="+" />
              </p>
              <p className="mt-1 text-xs font-medium uppercase tracking-wide text-white/60">Products traded</p>
            </div>
            <div>
              <p className="text-2xl font-extrabold sm:text-3xl">
                <Counter value={tradingCategories.length} />
              </p>
              <p className="mt-1 text-xs font-medium uppercase tracking-wide text-white/60">Categories</p>
            </div>
            <div>
              <p className="text-2xl font-extrabold sm:text-3xl">33 kV</p>
              <p className="mt-1 text-xs font-medium uppercase tracking-wide text-white/60">Voltage range</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================= */}
      {/* ABOUT OUR TRADING DIVISION                                    */}
      {/* ============================================================= */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
          <Reveal direction="right">
            <div className="overflow-hidden rounded-3xl shadow-sm">
              <img
                src={overviewImg}
                alt="Prithviraj trading division overview"
                className="h-72 w-full object-cover sm:h-96"
                loading="lazy"
              />
            </div>
          </Reveal>

          <Reveal direction="left" delay={120}>
            <h2 className="break-words text-4xl font-extrabold uppercase leading-none sm:text-5xl lg:text-6xl">
              <span className="hero-shimmer block text-brand-orange">Our Trading Division</span>
            </h2>
            <h3 className="mt-4 text-2xl font-bold leading-tight text-navy sm:text-3xl lg:text-4xl">
              Trusted Trading Partner for Electrical Infrastructure
            </h3>
            <p className="mt-4 text-slate-600">
              Prithviraj Corporation Limited supplies an extensive portfolio of electrical transmission
              and distribution products from certified and trusted manufacturers. Our strong supply
              network enables us to provide quality products, competitive pricing, timely delivery, and
              reliable technical support for projects of every scale.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {aboutFeatures.map((f) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.title}
                    className="group flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-brand-orange/30 hover:shadow-lg"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange transition duration-300 group-hover:scale-110 group-hover:bg-brand-orange group-hover:text-white">
                      <Icon size={18} />
                    </span>
                    <span className="text-sm font-bold text-navy">{f.title}</span>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================================================= */}
      {/* TRADING PRODUCT CATEGORIES                                    */}
      {/* ============================================================= */}
      <section className="bg-slate-50 py-16 sm:py-20">
        <div className="px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Product Categories"
              title="Everything Your Project Needs, From One Supplier"
            />
          </Reveal>
        </div>

        <div className="mx-auto mt-12 grid max-w-7xl gap-6 px-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-3 lg:px-8">
          {productCategories.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <Reveal key={cat.title} delay={i * 90}>
                <div className="group relative h-full overflow-hidden rounded-3xl border-2 border-transparent bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-brand-orange hover:shadow-xl hover:shadow-navy/5">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy/5 text-navy transition duration-300 group-hover:scale-110 group-hover:bg-brand-orange group-hover:text-white">
                    <Icon size={26} />
                  </div>
                  <h3 className="mt-5 text-lg font-extrabold text-navy">{cat.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{cat.desc}</p>
                  <ul className="mt-5 space-y-2 border-t border-slate-100 pt-4">
                    {cat.items.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                        <FiCheckCircle className="shrink-0 text-brand-orange" size={15} />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <span className="pointer-events-none absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-brand-orange transition-transform duration-300 group-hover:scale-x-100" />
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ============================================================= */}
      {/* WHY CHOOSE OUR TRADING DIVISION                                */}
      {/* ============================================================= */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <Reveal>
          <SectionHeading eyebrow="Why Choose Us" title="Built For Reliability, Priced For Scale" />
        </Reveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {whyChoose.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6 }}
                className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-navy to-navy-900 p-7 text-white shadow-lg"
              >
                <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-brand-orange/20 blur-2xl transition duration-500 group-hover:bg-brand-orange/40" />
                <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-orange/15 text-brand-orange">
                  <Icon size={26} />
                </div>
                <h3 className="relative mt-5 text-lg font-extrabold">{item.title}</h3>
                <p className="relative mt-2 text-sm leading-relaxed text-white/70">{item.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ============================================================= */}
      {/* INDUSTRIES WE SERVE                                           */}
      {/* ============================================================= */}
      <section className="bg-slate-50 py-16 sm:py-20" style={dotGridStyle}>
        <div className="px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHeading eyebrow="Industries We Serve" title="Supplying Across Every Corner Of The Power Sector" />
          </Reveal>
        </div>

        <div className="mx-auto mt-12 grid max-w-7xl gap-5 px-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-3 lg:px-8">
          {industries.map((ind, i) => {
            const Icon = ind.icon;
            return (
              <Reveal key={ind.title} delay={i * 80}>
                <div className="group flex h-full items-start gap-4 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100 transition duration-300 hover:-translate-y-1.5 hover:shadow-lg">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange transition duration-300 group-hover:scale-110 group-hover:bg-brand-orange group-hover:text-white">
                    <Icon size={22} />
                  </span>
                  <div>
                    <h3 className="font-extrabold text-navy">{ind.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{ind.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ============================================================= */}
      {/* TRADING PROCESS                                               */}
      {/* ============================================================= */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <Reveal>
          <SectionHeading eyebrow="Trading Process" title="From Requirement To Delivery, Fully Tracked" />
        </Reveal>

        {/* Desktop: horizontal timeline */}
        <div className="relative mt-16 hidden lg:block">
          <div className="absolute left-0 right-0 top-7 h-0.5 bg-slate-200">
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              style={{ transformOrigin: 'left' }}
              className="h-full bg-brand-orange"
            />
          </div>
          <div className="grid grid-cols-7 gap-3">
            {processSteps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                  className="flex flex-col items-center text-center"
                >
                  <span className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-navy text-brand-orange shadow-md ring-4 ring-white">
                    <Icon size={22} />
                  </span>
                  <p className="mt-4 text-xs font-bold leading-snug text-navy">{step.title}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Mobile / tablet: vertical timeline */}
        <div className="relative mt-12 space-y-8 lg:hidden">
          <div className="absolute bottom-2 left-7 top-2 w-0.5 bg-slate-200">
            <motion.div
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              style={{ transformOrigin: 'top' }}
              className="h-full w-full bg-brand-orange"
            />
          </div>
          {processSteps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.6 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative flex items-center gap-5 pl-1"
              >
                <span className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-navy text-brand-orange shadow-md ring-4 ring-white">
                  <Icon size={22} />
                </span>
                <p className="text-sm font-bold text-navy">{step.title}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ============================================================= */}
      {/* STATISTICS                                                    */}
      {/* ============================================================= */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-navy px-6 py-14 sm:px-10 sm:py-16">
          <Reveal>
            <SectionHeading eyebrow="By The Numbers" title="Our Trading Track Record" dark />
          </Reveal>

          <div className="mt-12 grid grid-cols-2 gap-y-10 gap-x-6 text-center sm:grid-cols-4">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 100}>
                <p className="text-3xl font-extrabold text-brand-orange sm:text-4xl">
                  <Counter value={s.value} suffix={s.suffix} />
                </p>
                <span className="mx-auto mt-3 block h-1 w-12 rounded-full bg-brand-orange/60" />
                <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-white/70 sm:text-sm">
                  {s.label}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================= */}
      {/* FAQ                                                           */}
      {/* ============================================================= */}
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <Reveal>
          <SectionHeading eyebrow="FAQ" title="Frequently Asked Questions" />
        </Reveal>

        <div className="mt-10 space-y-4">
          {faqs.map((item, i) => (
            <FaqItem key={item.q} item={item} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
          ))}
        </div>
      </section>

      {/* ============================================================= */}
      {/* FINAL CTA — full-width banner with parallax background        */}
      {/* ============================================================= */}
      <section className="relative overflow-hidden py-24 text-white sm:py-32">
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.15 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        >
         
          <div className="absolute inset-0 bg-navy/80" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
        </motion.div>

        <Reveal className="relative mx-auto flex max-w-3xl flex-col items-center gap-4 px-4 text-center sm:px-6 lg:px-8">
          <h2 className="break-words text-3xl font-extrabold uppercase leading-none sm:text-4xl lg:text-5xl">
            <span className="hero-shimmer block text-brand-orange">Reliable Trading Solutions</span>
          </h2>
          <p className="max-w-xl text-white/80">
            Partner with Prithviraj Corporation Limited for quality electrical transmission and
            distribution products, competitive pricing, and dependable nationwide delivery.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition hover:-translate-y-0.5 hover:bg-brand-orangedark"
            >
              Request a Quote <FiArrowRight />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition hover:-translate-y-0.5 hover:border-white"
            >
              <FiPhoneCall /> Contact Us
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
